/**
 * DashboardPage Component
 * 대시보드 메인 레이아웃 페이지
 */
import React, {useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import Sidebar from '../../layout/Sidebar/Sidebar';
import Header from '../../layout/Header/Header';
import DashboardMainContent from './DashboardMainContent';
import {getItem, setItem} from '@/shared/utils/storage';
import {authApi} from '@/features/auth/api/authApi';
import {useToast} from '@/shared/components/feedback/Toast/ToastContext';
import {ToastType} from '@/shared/components/feedback/Toast/Toast.types';
import {clearFilters} from '@/shared/utils/filterStorage';
import {getPageLabelByType} from '@/features/dashboard/config/pageLabels.config';
import {getFilterKeysByTabId} from '@/features/dashboard/config/filterKeys.config';
import type {Tab} from '@/features/dashboard/types/dashboard.types';
import FeeManagement from '@/features/partner/supplierFee/toHeadquarters/components/FeeManagement';
import DeductionPartnerToHeadPage
    from '@/features/settlement/deduction/partnerToHead/components/DeductionPartnerToHeadPage';
import DeductionRiderToHeadPage
    from '@/features/settlement/deduction/riderToHead/components/DeductionRiderToHeadPage';
import DeductionRiderToSupplierPage
    from '@/features/settlement/deduction/riderToPartner/components/DeductionRiderToSupplierPage';
import PromotionHeadToSupplierPage
    from '@/features/settlement/promotion/headToPartner/components/PromotionHeadToSupplierPage';
import PromotionHeadToRiderPage
    from '@/features/settlement/promotion/headToRider/components/PromotionHeadToRiderPage';
import PromotionSupplierToRiderPage
    from '@/features/settlement/promotion/partnerToRider/components/PromotionSupplierToRiderPage';
import PartnerSettlementHistory
    from '@/features/settlement/settlementHistory/partnerSettlement/components/PartnerSettlementHistory';
import RiderSettlementHistory
    from '@/features/settlement/settlementHistory/riderSettlement/components/RiderSettlementHistory';
import '@/shared/styles/search-form.css';
import '../../../styles/DashboardPage.css';
import RiderStatusPage from '@/features/rider/riderStatus/components/RiderStatusPage';
import RiderDetailInfoPage from '@/features/rider/riderInfoDetail/components/RiderInfoDetailPage';
import AccountingManagement
    from '@/features/system/withdrawRestriction/components/PartnerAccountingPage';
import VirtualAccountManagement from '@/features/system/virtualAccount/components/VirtualAccountManagement';
import RiderAccountingPage from '@/features/system/withdrawRestriction/components/RiderAccountingPage';
import DeliveryHistoryPage from '@/features/rider/deliveryHistory/components/DeliveryHistoryPage';
import DeliveryHistoryDetailPage from '@/features/rider/deliveryHistoryDetail/components/DeliveryHistoryDetailPage';
import SupplierManagement from '@/features/partner/supplierStatus/components/SupplierManagement';
import PartnerFeeToHeadPage from '@/features/partner/supplierFee/toHeadquarters/components/PartnerFeeToHeadPage';
import RiderFeeToSupplierPage from '@/features/partner/supplierFee/riderToPartner/components/RiderFeeToSupplierPage';
import RiderPreSettlementHistory
    from '@/features/settlement/settlementHistory/riderPreSettlement/components/RiderPreSettlementHistory';
import HeadWithdrawHistory from '@/features/settlement/settlementHistory/headWithdraw/components/HeadWithdrawHistory';
import TransferHistory from '@/features/system/transfer/components/TransferHistory';
import UserManagementPage from '@/features/system/userManagement/components/UserManagementPage';
import SystemCommonCodeManagementPage
    from '@/features/system/commonCode/components/SystemCommonCodeManagementPage';
import SystemCommonCodeCreatePage
    from '@/features/system/commonCode/components/SystemCommonCodeCreatePage';
import UserWithdrawRequestPage from '@/features/system/userWithdraw/components/UserWithdrawRequestPage';
import ReSettlementPage from '@/features/system/reSettlement/components/ReSettlementPage';
import RiderWorkStatusPage from '@/features/settlement/riderWorkStatus/components/RiderWorkStatusPage';
import MaintenanceManagementPage from '@/features/system/maintenance/components/MaintenanceManagementPage';
import MaintenanceScreen from '@/features/system/maintenance/components/MaintenanceScreen';
import BalanceSnapshotPage from "@/features/system/balanceSnapshot/components/BalanceSnapshotPage";
import {useActiveMaintenance} from '@/features/system/maintenance/hooks/useMaintenanceData';
import NoticeManagementPage from "@/features/notice/components/pages/NoticeManagementPage";
import SettlementUploadPage from "@/features/system/settlementUpload/components/SettlementUploadPage";
import AuthorityManagementPage from "@/features/system/authority/components/AuthorityManagementPage";
import {NoticePopupModal} from "@/features/notice/components/modals/NoticePopupModal";
import {noticeApi} from "@/features/notice/api/noticeApi";
import Footer from "@/features/dashboard/components/layout/Footer/Footer";
import {Notice, noticeListFromDto} from "@/features/notice/types/notice.types";
import {format} from "date-fns";

// 오늘 하루 보지 않기 localStorage 키
const NOTICE_HIDDEN_TODAY_KEY = 'NOTICE_HIDDEN_TODAY';

// 오늘 숨긴 공지 ID 목록 가져오기
const getHiddenNoticeIdsForToday = (): number[] => {
    try {
        const stored = localStorage.getItem(NOTICE_HIDDEN_TODAY_KEY);
        if (!stored) return [];

        const data = JSON.parse(stored);
        const today = format(new Date(), 'yyyy-MM-dd');

        // 오늘 날짜와 일치하는 경우에만 반환
        if (data.date === today) {
            return data.noticeIds || [];
        }
        // 날짜가 다르면 초기화
        localStorage.removeItem(NOTICE_HIDDEN_TODAY_KEY);
        return [];
    } catch {
        return [];
    }
};

// 오늘 하루 보지 않기 저장
const saveHiddenNoticeForToday = (noticeId: number): void => {
    try {
        const today = format(new Date(), 'yyyy-MM-dd');
        const existingIds = getHiddenNoticeIdsForToday();

        if (!existingIds.includes(noticeId)) {
            existingIds.push(noticeId);
        }

        localStorage.setItem(NOTICE_HIDDEN_TODAY_KEY, JSON.stringify({
            date: today,
            noticeIds: existingIds
        }));
    } catch (error) {
        console.error('오늘 하루 보지 않기 저장 실패:', error);
    }
};

const DashboardPage: React.FC = () => {
    const location = useLocation();

    // localStorage에서 탭 상태 복원
    const [currentPage, setCurrentPage] = useState<string>(() => {
        return getItem<string>('CURRENT_PAGE') || 'dashboard';
    });
    const [tabs, setTabs] = useState<Tab[]>(() => {
        const savedTabs = getItem<Tab[]>('DASHBOARD_TABS');
        return savedTabs && savedTabs.length > 0
            ? savedTabs
            : [{id: 'dashboard', label: '대시보드', page: 'dashboard'}];
    });
    const [activeTabId, setActiveTabId] = useState<string>(() => {
        return getItem<string>('ACTIVE_TAB_ID') || 'dashboard';
    });
    const [selectedRiderId, setSelectedRiderId] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState({
        userName: '',
        userEmail: '',
        userType: 'HEADQUARTERS',
    });
    const [isUserInfoLoading, setIsUserInfoLoading] = useState(true);
    const {showToast} = useToast();

    // 점검 상태 조회 (협력사 사용자용)
    const {activeMaintenance, isMaintenance} = useActiveMaintenance();

    // 점검 화면 확인 여부 (세션당 한 번만 표시)
    const [maintenanceAcknowledged, setMaintenanceAcknowledged] = useState(() => {
        const acknowledged = sessionStorage.getItem('maintenance_acknowledged');
        const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
        // 점검 ID가 변경되었으면 확인 상태 초기화
        if (activeMaintenance && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
            sessionStorage.removeItem('maintenance_acknowledged');
            return false;
        }
        return acknowledged === 'true';
    });

    // 점검 상태가 변경되면 확인 상태 초기화
    useEffect(() => {
        if (!isMaintenance) {
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');
            setMaintenanceAcknowledged(false);
        } else if (activeMaintenance) {
            // 점검 ID가 변경되었는지 확인
            const acknowledgedSeqNo = sessionStorage.getItem('maintenance_acknowledged_seqno');
            if (acknowledgedSeqNo && acknowledgedSeqNo !== String(activeMaintenance.seqNo)) {
                sessionStorage.removeItem('maintenance_acknowledged');
                sessionStorage.removeItem('maintenance_acknowledged_seqno');
                setMaintenanceAcknowledged(false);
            }
        }
    }, [isMaintenance, activeMaintenance]);

    // 협력사 사용자이고 점검 중인지 확인 (localStorage에서 직접 읽어서 초기 렌더링 시에도 정확하게 판단)
    const currentUserType = getItem<string>('USER_TYPE') || userInfo.userType;
    
    // 점검 중 접근 가능한 페이지 목록 (대시보드만 허용)
    const allowedPagesDuringMaintenance = [
        'dashboard'
    ];
    
    // 협력사 사용자이고 점검 중이며, 현재 페이지가 허용되지 않은 페이지인 경우 점검 화면 표시
    const isSupplierUnderMaintenance = 
        currentUserType === 'SUPPLIER' && 
        isMaintenance && 
        !maintenanceAcknowledged &&
        !allowedPagesDuringMaintenance.includes(currentPage);

    // 팝업 공지 상태 (각 공지의 표시 여부를 개별 관리)
    const [popupNotices, setPopupNotices] = useState<Notice[]>([]);
    const [visibleNoticeIds, setVisibleNoticeIds] = useState<Set<number>>(new Set());
    // 팝업 z-index 관리 (클릭한 팝업이 맨 앞으로)
    const [noticeZIndexMap, setNoticeZIndexMap] = useState<Map<number, number>>(new Map());
    const [maxZIndex, setMaxZIndex] = useState(10000);

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userId = getItem<string>('USER_ID');

                if (!userId) {
                    console.error('로그인 정보가 없습니다.');
                    showToast('로그인 정보를 찾을 수 없습니다.', {type: ToastType.ERROR});
                    setIsUserInfoLoading(false);
                    return;
                }

                const response = await authApi.getUserInfo(userId);
                const userType = getItem<string>('USER_TYPE') || 'HEADQUARTERS';

                setUserInfo({
                    userName: response.userName,
                    userEmail: response.emailInfo,
                    userType: userType,
                });
                setIsUserInfoLoading(false);
            } catch (error) {
                console.error('사용자 정보 조회 실패:', error);
                showToast('사용자 정보를 불러오는데 실패했습니다.', {type: ToastType.ERROR});

                const userName = getItem<string>('USER_NAME') || '';
                const userType = getItem<string>('USER_TYPE') || 'HEADQUARTERS';

                setUserInfo({
                    userName,
                    userEmail: '',
                    userType,
                });
                setIsUserInfoLoading(false);
            }
        };

        fetchUserInfo();
    }, [showToast]);

    // 팝업 공지 조회 (협력사 사용자일 때만)
    useEffect(() => {
        const fetchPopupNotices = async () => {
            // 협력사 사용자만 팝업 공지 조회
            const userType = getItem<string>('USER_TYPE');
            if (userType !== 'SUPPLIER') return;

            try {
                const response = await noticeApi.getUnreadPopupNotices();
                const allNotices = noticeListFromDto(response);

                // 오늘 숨김 공지 필터링
                const hiddenIds = getHiddenNoticeIdsForToday();
                const notices = allNotices.filter(n => !hiddenIds.includes(n.noticeId));

                if (notices.length > 0) {
                    setPopupNotices(notices);
                    // 모든 공지를 표시 상태로 설정
                    setVisibleNoticeIds(new Set(notices.map(n => n.noticeId)));
                    // 초기 z-index 설정
                    const initialZIndexMap = new Map<number, number>();
                    notices.forEach((n, idx) => {
                        initialZIndexMap.set(n.noticeId, 10000 + idx);
                    });
                    setNoticeZIndexMap(initialZIndexMap);
                    setMaxZIndex(10000 + notices.length);
                }
            } catch (error) {
                console.error('팝업 공지 조회 실패:', error);
            }
        };

        // 사용자 정보 로딩 완료 후 팝업 공지 조회
        if (!isUserInfoLoading) {
            fetchPopupNotices();
        }
    }, [isUserInfoLoading]);

    // 오늘 하루 보지 않기 처리 (localStorage에 저장)
    const handleHideForToday = useCallback((noticeId: number) => {
        saveHiddenNoticeForToday(noticeId);
    }, []);

    // 점검 화면 확인 처리
    const handleMaintenanceAcknowledge = () => {
        if (activeMaintenance) {
            sessionStorage.setItem('maintenance_acknowledged', 'true');
            sessionStorage.setItem('maintenance_acknowledged_seqno', String(activeMaintenance.seqNo));
            setMaintenanceAcknowledged(true);
        }
    };

    // 공지 팝업 맨 앞으로 가져오기
    const handleBringToFront = useCallback((noticeId: number) => {
        setMaxZIndex(prev => {
            const newMaxZIndex = prev + 1;
            setNoticeZIndexMap(prevMap => {
                const newMap = new Map(prevMap);
                newMap.set(noticeId, newMaxZIndex);
                return newMap;
            });
            return newMaxZIndex;
        });
    }, []);

    // 개별 공지 팝업 닫기
    const handleCloseNoticePopup = useCallback((noticeId: number) => {
        setVisibleNoticeIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(noticeId);
            return newSet;
        });
    }, []);

    useEffect(() => {
        if (location.state) {
            const {page, riderId} = location.state as { page?: string; riderId?: string };

            if (page === 'rider_detail' && riderId) {
                setCurrentPage('rider_detail');
                setSelectedRiderId(riderId);
            }
        }
    }, [location.state]);

    useEffect(() => {
        setItem('DASHBOARD_TABS', tabs);
        setItem('ACTIVE_TAB_ID', activeTabId);
        setItem('CURRENT_PAGE', currentPage);
    }, [tabs, activeTabId, currentPage]);

    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            const userId = getItem<string>('USER_ID');

            if (userId && location.pathname === '/dashboard') {
                const state = event.state as { page?: string } | null;

                if (state?.page) {
                    const tab = tabs.find((t) => t.page === state.page);
                    if (tab) {
                        setActiveTabId(tab.id);
                        setCurrentPage(tab.page);
                        return;
                    }
                }

                const dashboardTab = tabs.find((tab) => tab.id === 'dashboard');
                if (!dashboardTab) {
                    setTabs([{id: 'dashboard', label: '대시보드', page: 'dashboard'}, ...tabs]);
                }
                setActiveTabId('dashboard');
                setCurrentPage('dashboard');
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [tabs, location.pathname]);

    const getPageLabel = useCallback(
        (page: string): string => {
            const isEditMode =
                page === 'common_code_add' ? getItem<string>('COMMON_CODE_EDIT_MODE') === 'true' : undefined;
            return getPageLabelByType(page, userInfo.userType, isEditMode);
        },
        [userInfo.userType]
    );

    const handlePageChange = useCallback(
        (page: string) => {
            setTabs((prevTabs) => {
                const existingTab = prevTabs.find((tab) => tab.page === page);

                if (existingTab) {
                    const updatedLabel = getPageLabel(page);
                    if (existingTab.label !== updatedLabel) {
                        return prevTabs.map((tab) =>
                            tab.id === existingTab.id ? {...tab, label: updatedLabel} : tab
                        );
                    }
                    setActiveTabId(existingTab.id);
                    setCurrentPage(page);
                    return prevTabs;
                }

                const newTab: Tab = {
                    id: page,
                    label: getPageLabel(page),
                    page: page,
                };
                setActiveTabId(page);
                setCurrentPage(page);
                return [...prevTabs, newTab];
            });

            window.history.pushState({page}, '', '/dashboard');
        },
        [getPageLabel]
    );

    const handleTabClick = (tabId: string) => {
        const tab = tabs.find((t) => t.id === tabId);
        if (tab) {
            setActiveTabId(tabId);
            setCurrentPage(tab.page);
            window.history.pushState({page: tab.page}, '', '/dashboard');
        }
    };

    const handleCloseTab = useCallback(
        (tabId: string, e?: React.MouseEvent) => {
            if (e) {
                e.stopPropagation();
            }

            const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
            if (tabIndex === -1) return;

            if (tabId === 'dashboard') return;

            const filterKeys = getFilterKeysByTabId(tabId);
            if (filterKeys) {
                filterKeys.forEach((key) => clearFilters(key));
            }

            const newTabs = tabs.filter((tab) => tab.id !== tabId);
            setTabs(newTabs);

            if (activeTabId === tabId) {
                if (tabIndex > 0) {
                    const prevTab = newTabs[tabIndex - 1];
                    setActiveTabId(prevTab.id);
                    setCurrentPage(prevTab.page);
                } else if (newTabs.length > 0) {
                    const nextTab = newTabs[0];
                    setActiveTabId(nextTab.id);
                    setCurrentPage(nextTab.page);
                }
            }
        },
        [tabs, activeTabId]
    );

    const handleCloseAllTabs = () => {
        tabs.forEach((tab) => {
            const filterKeys = getFilterKeysByTabId(tab.id);
            if (filterKeys) {
                filterKeys.forEach((key) => clearFilters(key));
            }
        });

        const dashboardTab = tabs.find((tab) => tab.id === 'dashboard');
        if (dashboardTab) {
            setTabs([dashboardTab]);
            setActiveTabId('dashboard');
            setCurrentPage('dashboard');
        }
    };

    useEffect(() => {
        const handleDashboardNavigate = (event: Event) => {
            const customEvent = event as CustomEvent<{ page?: string }>;
            if (customEvent.detail?.page) {
                handlePageChange(customEvent.detail.page);
            }
        };

        const handleCloseTabEvent = (event: Event) => {
            const customEvent = event as CustomEvent<{ tabId?: string }>;
            if (customEvent.detail?.tabId) {
                handleCloseTab(customEvent.detail.tabId);
            }
        };

        window.addEventListener('dashboard:navigate', handleDashboardNavigate);
        window.addEventListener('dashboard:close-tab', handleCloseTabEvent);
        return () => {
            window.removeEventListener('dashboard:navigate', handleDashboardNavigate);
            window.removeEventListener('dashboard:close-tab', handleCloseTabEvent);
        };
    }, [handlePageChange, handleCloseTab]);

    const renderPageContent = () => {
        switch (currentPage) {
            case 'dashboard':
                return <DashboardMainContent userType={userInfo.userType}/>;
            case 'delivery_history':
                return <DeliveryHistoryPage/>;
            case 'delivery_history_detail':
                return <DeliveryHistoryDetailPage/>;
            case 'rider_status':
                return <RiderStatusPage/>;
            case 'rider_detail':
                return <RiderDetailInfoPage riderId={selectedRiderId || undefined}/>;
            case 'rider_work_status':
                if (userInfo.userType !== 'HEADQUARTERS') {
                    showToast('접근 권한이 없습니다.', {type: ToastType.WARNING});
                    handlePageChange('dashboard');
                    return <DashboardMainContent userType={userInfo.userType}/>;
                }
                return <RiderWorkStatusPage/>;
            case 'partner_status':
                return <SupplierManagement/>;
            case 'partner_fee_to_head':
                return <PartnerFeeToHeadPage/>;
            case 'rider_fee_to_supplier':
                return <RiderFeeToSupplierPage/>;
            case 'partner_settlement':
                return <PartnerSettlementHistory/>;
            case 'transaction_history':
                return <HeadWithdrawHistory/>;
            case 'virtual_account':
                if (userInfo.userType !== 'HEADQUARTERS') {
                    showToast('접근 권한이 없습니다.', {type: ToastType.WARNING});
                    handlePageChange('dashboard');
                    return <DashboardMainContent userType={userInfo.userType}/>;
                }
                return <VirtualAccountManagement/>;
            case 'deduction_partner_to_head':
                return <DeductionPartnerToHeadPage/>;
            case 'deduction_rider_to_head':
                return <DeductionRiderToHeadPage/>;
            case 'deduction_rider_to_supplier':
                return <DeductionRiderToSupplierPage/>;
            case 'promotion_head_to_supplier':
                return <PromotionHeadToSupplierPage/>;
            case 'promotion_head_to_rider':
                return <PromotionHeadToRiderPage/>;
            case 'promotion_supplier_to_rider':
                return <PromotionSupplierToRiderPage/>;
            case 'fee_management':
                return <FeeManagement/>;
            case 'withdrawal_restriction': {
                const currentUserType = getItem<string>('USER_TYPE') || 'HEADQUARTERS';
                if (currentUserType === 'SUPPLIER') {
                    return <RiderAccountingPage/>;
                } else {
                    return <AccountingManagement/>;
                }
            }
            case 'transfer_management':
                if (userInfo.userType !== 'HEADQUARTERS') {
                    showToast('접근 권한이 없습니다.', {type: ToastType.WARNING});
                    handlePageChange('dashboard');
                    return <DashboardMainContent userType={userInfo.userType}/>;
                }
                return <TransferHistory/>;
            case 'settlement_history':
                return <PartnerSettlementHistory/>;
            case 'rider_history_week':
                return <RiderSettlementHistory/>;
            case 'rider_history_pre':
                return <RiderPreSettlementHistory/>;
            case 'user_management':
                return <UserManagementPage/>;
            case 'withdraw_request':
                return <UserWithdrawRequestPage/>;
            case 'authority_management':
                return <AuthorityManagementPage />;
            case 'settlement_upload':
                return <SettlementUploadPage/>;
            case 'system_settings':
                return <div className="page-placeholder">시스템 설정 페이지</div>;
            case 'backup_restore':
                return <div className="page-placeholder">백업/복구 페이지</div>;
            case 'common_code':
                return <SystemCommonCodeManagementPage/>;
            case 'common_code_add':
                return <SystemCommonCodeCreatePage/>;
            case 're_settlement':
                return <ReSettlementPage/>;
            case 'notice_management':
                // 공지 관리: 협력사/본사/관리자 모두 접근 가능 (사이드바에서 가맹 협력사는 이미 필터링됨)
                return <NoticeManagementPage />;
            case 'maintenance_management': {
                // 점검 관리: 본사(HEADQUARTERS)만 접근 가능
                const currentUserType = getItem<string>('USER_TYPE');
                if (currentUserType !== 'HEADQUARTERS') {
                    showToast('접근 권한이 없습니다.', {type: ToastType.WARNING});
                    handlePageChange('dashboard');
                    return <DashboardMainContent userType={userInfo.userType}/>;
                }
                return <MaintenanceManagementPage/>;
            }
            case 'balance_snapshot': {
                const currentUserType = getItem<string>('USER_TYPE');
                if (currentUserType !== 'HEADQUARTERS') {
                    showToast('접근 권한이 없습니다.', { type: ToastType.WARNING });
                    handlePageChange('dashboard');
                    return <DashboardMainContent userType={userInfo.userType}/>;
                }
                return <BalanceSnapshotPage />
            }
            default:
                return <div className="page-placeholder">페이지를 찾을 수 없습니다</div>;
        }
    };

    return (
        <div className="dashboard-layout">
            <Sidebar onPageChange={handlePageChange} currentPage={currentPage}/>

            {/* 팝업 공지 모달 - 각 공지마다 별도의 팝업 */}
            {popupNotices.map((notice, index) => (
                visibleNoticeIds.has(notice.noticeId) && (
                    <NoticePopupModal
                        key={notice.noticeId}
                        notice={notice}
                        index={index}
                        zIndex={noticeZIndexMap.get(notice.noticeId) || 10000 + index}
                        onClose={() => handleCloseNoticePopup(notice.noticeId)}
                        onHideForToday={handleHideForToday}
                        onBringToFront={handleBringToFront}
                    />
                )
            ))}

            {/* 점검 중이고 아직 확인하지 않았으면 MaintenanceScreen 전체 화면 표시 */}
            {currentUserType === 'SUPPLIER' && isMaintenance && activeMaintenance && !maintenanceAcknowledged ? (
                <MaintenanceScreen 
                    maintenance={activeMaintenance} 
                    showDashboardButton={true}
                    onDashboardClick={handleMaintenanceAcknowledge}
                />
            ) : (
                <div className="dashboard-main">
                    <Header
                        userName={isUserInfoLoading ? undefined : userInfo.userName}
                        userEmail={isUserInfoLoading ? undefined : userInfo.userEmail}
                        userType={userInfo.userType}
                    />

                    <div className="tabs-container">
                        <div className="tabs-header">
                            {tabs.map((tab) => {
                                // 점검 중일 때 대시보드가 아닌 탭은 비활성화
                                const isTabDisabled = currentUserType === 'SUPPLIER' && 
                                                     isMaintenance && 
                                                     tab.page !== 'dashboard';
                                
                                return (
                                    <div
                                        key={tab.id}
                                        className={`tab ${activeTabId === tab.id ? 'active' : ''} ${isTabDisabled ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (isTabDisabled) return;
                                            handleTabClick(tab.id);
                                        }}
                                        style={isTabDisabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                                    >
                                        <span className="tab-label">{getPageLabel(tab.page)}</span>
                                        {tab.id !== 'dashboard' && (
                                            <button 
                                                className="tab-close" 
                                                onClick={(e) => {
                                                    if (isTabDisabled) {
                                                        e.stopPropagation();
                                                        return;
                                                    }
                                                    handleCloseTab(tab.id, e);
                                                }}
                                                disabled={isTabDisabled}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {tabs.length > 1 && (
                                <button className="close-all-tabs-btn" onClick={handleCloseAllTabs}>
                                    <i className="fas fa-times-circle"></i>
                                    <span>전체 닫기</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="dashboard-content">
                        {isSupplierUnderMaintenance && activeMaintenance ? (
                            <MaintenanceScreen
                                maintenance={activeMaintenance}
                                showDashboardButton={true}
                                onDashboardClick={handleMaintenanceAcknowledge}
                            />
                        ) : (
                            renderPageContent()
                        )}
                    </div>
                    <Footer />
                </div>
            )}
        </div>
    );
};

export default DashboardPage;
